import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { Puzzle, BookOpen, ChartNoAxesCombined, CalendarCheck, Activity, Bell, BriefcaseBusiness, CalendarDays, ChevronRight, Database, Flag, ClipboardList, FileText, Gauge, KeyRound, LayoutDashboard, LockKeyhole, LogOut, Megaphone, Menu, Moon, Radar, Search, ScrollText, Settings, ShieldCheck, SlidersHorizontal, Sun, UserRound, Users, Workflow, Wallet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
const USER_NAV = [{
  to: "/copilot",
  label: "Application Copilot",
  icon: Puzzle
}, {
  to: "/dashboard",
  label: "Overview",
  icon: LayoutDashboard
}, {
  to: "/tasks",
  label: "Today & tasks",
  icon: CalendarCheck
}, {
  to: "/jobs",
  label: "Find opportunities",
  icon: BriefcaseBusiness
}, {
  to: "/tracker",
  label: "Applications",
  icon: ClipboardList
}, {
  to: "/studio",
  label: "Document studio",
  icon: FileText
}, {
  to: "/interviews",
  label: "Interview planner",
  icon: CalendarDays
}, {
  to: "/practice",
  label: "Interview practice",
  icon: BookOpen
}, {
  to: "/offers",
  label: "Offers & decisions",
  icon: Wallet
}, {
  to: "/insights",
  label: "Personal insights",
  icon: ChartNoAxesCombined
}, {
  to: "/profile",
  label: "Career profile",
  icon: UserRound
}, {
  to: "/rules",
  label: "Search rules",
  icon: SlidersHorizontal
}, {
  to: "/settings",
  label: "Settings & privacy",
  icon: Settings
}];
const ADMIN_NAV = [{
  to: "/admin",
  label: "Command centre",
  icon: Gauge,
  end: true,
  scope: "dashboard.read"
}, {
  to: "/admin/users",
  label: "Users & accounts",
  icon: Users,
  scope: "users.read"
}, {
  to: "/admin/access",
  label: "Access policies",
  icon: LockKeyhole,
  scope: "dashboard.read"
}, {
  to: "/admin/providers",
  label: "AI providers",
  icon: KeyRound,
  scope: "ai.read"
}, {
  to: "/admin/routes",
  label: "Models & benchmarks",
  icon: Workflow,
  scope: "ai.read"
}, {
  to: "/admin/usage",
  label: "Usage & budgets",
  icon: Wallet,
  scope: "usage.read"
}, {
  to: "/admin/sources",
  label: "Discovery sources",
  icon: Radar,
  scope: "sources.read"
}, {
  to: "/admin/system",
  label: "System & controls",
  icon: Activity,
  scope: "monitoring.read"
}, {
  to: "/admin/jobs",
  label: "Background jobs",
  icon: Workflow,
  scope: "monitoring.read"
}, {
  to: "/admin/incidents",
  label: "Incidents",
  icon: Flag,
  scope: "incidents.manage"
}, {
  to: "/admin/communications",
  label: "Announcements",
  icon: Megaphone,
  scope: "communications.manage"
}, {
  to: "/admin/governance",
  label: "Data governance",
  icon: Database,
  scope: "governance.manage"
}, {
  to: "/admin/audit",
  label: "Audit trail",
  icon: ScrollText,
  scope: "audit.read"
}];
function NavItem({
  item,
  onClick
}) {
  const Icon = item.icon;
  return <NavLink to={item.to} end={item.end} onClick={onClick} data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={({
    isActive
  }) => cn("workspace-nav", isActive && "workspace-nav-active")}><Icon size={18} aria-hidden /><span>{item.label}</span></NavLink>;
}
export default function AppShell() {
  const {
    me,
    signOut,
    demo
  } = useAuth();
  const {
    resolvedTheme,
    setTheme
  } = useTheme();
  const [unread, setUnread] = useState(0);
  const [menu, setMenu] = useState(false);
  const [query, setQuery] = useState("");
  const loc = useLocation();
  const nav = useNavigate();
  const isAdmin = me?.role === "super_admin" || !!me?.admin_permissions?.length;
  const adminNav = ADMIN_NAV.filter(item => me?.role === "super_admin" || me?.admin_permissions?.includes(item.scope));
  const inAdmin = loc.pathname.startsWith("/admin");
  const active = [...USER_NAV, ...ADMIN_NAV].find(item => item.to === loc.pathname)?.label || "Workspace";
  useEffect(() => {
    let live = true;
    const load = () => api.get("/notifications").then(r => {
      if (live) setUnread(r.data.unread);
    }).catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [loc.pathname]);
  useEffect(() => {
    setMenu(false);
  }, [loc.pathname]);
  const sidebar = <>
    <Link to="/dashboard" className="workspace-logo" onClick={() => setMenu(false)}><span className="brand-symbol"><BriefcaseBusiness size={21} /></span><span>AJOBF<span className="block text-[11px] font-normal tracking-normal text-slate-400">Your next chapter.</span></span></Link>
    <div className="mb-7 rounded-xl border border-white/10 bg-white/5 p-3.5"><p className="text-xs text-slate-400">{inAdmin ? "Operations workspace" : "Personal workspace"}</p><p className="mt-1 truncate text-sm font-medium text-white">{me?.preferences?.full_name || me?.name || "Your career workspace"}</p><span className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-blue-200"><span className="h-1.5 w-1.5 rounded-full bg-blue-300" />{demo ? "Local demo" : "Private account"}</span></div>
    {inAdmin && <Link to="/dashboard" className="mb-4 flex items-center gap-2 px-3 text-xs text-slate-400"><ChevronRight size={13} className="rotate-180" />Personal workspace</Link>}
    <nav className="space-y-1" aria-label={inAdmin ? "Administration" : "Workspace"}>{(inAdmin ? adminNav : USER_NAV).map(item => <NavItem key={item.to} item={item} onClick={() => setMenu(false)} />)}</nav>
    {isAdmin && !inAdmin && <details className="mt-6 border-t border-white/10 pt-4" open={inAdmin || undefined}><summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs text-slate-400"><ShieldCheck size={15} />Administration<ChevronRight size={14} className="ml-auto" /></summary><nav className="mt-2 space-y-1" aria-label="Administration">{adminNav.map(item => <NavItem key={item.to} item={item} onClick={() => setMenu(false)} />)}</nav></details>}
    <div className="mt-auto pt-8"><div className={`rounded-xl bg-blue-400/10 p-4 ${inAdmin ? "hidden" : ""}`}><ShieldCheck className="mb-2 text-blue-200" size={19} /><p className="text-xs font-medium text-blue-100">Your career. Your decisions.</p><p className="mt-1 text-[11px] leading-relaxed text-slate-400">Review your documents. Apply on the employer's site. Emails stay drafts.</p></div><div className="mt-5 flex items-center gap-2 border-t border-white/10 pt-4"><div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-700 text-xs text-white">{(me?.preferences?.full_name || me?.name || "U").slice(0, 1).toUpperCase()}</div><p className="min-w-0 flex-1 truncate text-[11px] text-slate-400" data-testid="sidebar-user-email">{me?.email}</p><button aria-label="Sign out" onClick={signOut} className="rounded p-2 text-slate-400 hover:text-white" data-testid="sign-out-button"><LogOut size={16} /></button></div></div>
  </>;
  return <Sheet open={menu} onOpenChange={setMenu}><div className="min-h-screen bg-background">
    <a href="#main-content" className="skip-link">Skip to content</a>
    <aside className="workspace-sidebar hidden lg:flex">{sidebar}</aside>
    <SheetContent side="left" className="!flex !w-[280px] !max-w-[90vw] flex-col overflow-y-auto border-0 bg-[#152441] px-4 py-6 text-slate-200"><SheetTitle className="sr-only">Workspace navigation</SheetTitle><SheetDescription className="sr-only">Choose a page in your career workspace.</SheetDescription>{sidebar}</SheetContent>
    <div className="lg:pl-[248px]">
      <header className="workspace-header">
        <div className="flex min-w-0 items-center gap-3"><SheetTrigger asChild><button className="icon-button lg:hidden" aria-label="Open navigation" aria-expanded={menu}><Menu size={20} /></button></SheetTrigger><span className="hidden text-xs text-muted-foreground sm:inline">Workspace</span><ChevronRight size={13} className="hidden text-muted-foreground sm:block" /><span className="truncate text-sm font-medium">{inAdmin ? "Administration" : active}</span></div>
        <div className="flex items-center gap-2 sm:gap-3">
          <form className={`${inAdmin ? "hidden" : "hidden lg:flex"} items-center gap-2 rounded-lg bg-muted/70 px-3`} onSubmit={e => {
              e.preventDefault();
              nav(`/jobs?q=${encodeURIComponent(query)}`);
            }}><Search size={15} className="text-muted-foreground" /><input aria-label="Search opportunities" placeholder="Search opportunities" value={query} onChange={e => setQuery(e.target.value)} className="h-9 w-44 bg-transparent text-xs outline-none" /></form>
          <NavLink to="/notifications" data-testid="notifications-bell" aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`} className="icon-button relative"><Bell size={18} />{unread > 0 && <span className="absolute right-0 top-0 grid min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] text-primary-foreground">{unread}</span>}</NavLink>
          <button className="icon-button" data-testid="theme-toggle" aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} theme`} onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}>{resolvedTheme === "dark" ? <Sun size={18} /> : <Moon size={18} />}</button>
          <Link to="/profile" aria-label="Open career profile" className="grid h-8 w-8 place-items-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{(me?.preferences?.full_name || me?.name || "U").slice(0, 1).toUpperCase()}</Link>
        </div>
      </header>
      <main id="main-content" className="mx-auto max-w-[1440px] px-4 py-7 pb-24 sm:px-8 lg:px-10 lg:py-9"><Outlet /></main>
    </div>
    <nav className="mobile-dock lg:hidden" aria-label="Quick navigation">{(inAdmin ? adminNav.filter(i => ["/admin", "/admin/system", "/admin/jobs", "/admin/incidents"].includes(i.to)) : USER_NAV.filter(i => ["/dashboard", "/tasks", "/jobs", "/tracker"].includes(i.to))).map(item => {
          const Icon = item.icon;
          return <NavLink key={item.to} to={item.to} className={({
            isActive
          }) => cn("flex flex-1 flex-col items-center gap-1 px-1 py-3 text-[10px]", isActive ? "text-primary" : "text-muted-foreground")}><Icon size={19} />{item.to === "/jobs" ? "Discover" : item.to === "/interviews" ? "Interviews" : item.label}</NavLink>;
        })}</nav>
  </div></Sheet>;
}
